/* hs-eslint ignored failing-rules */

/* eslint-disable no-bitwise */
'use es6';

import PropTypes from 'prop-types';
import ReactUtilsPropTypes from 'react-utils/CustomPropTypes';
import AnnotationType from 'react-codemirror/enum/AnnotationType';
const AnnotationTypeValueList = Object.values(AnnotationType);
const AnnotationTypeValueDict = AnnotationTypeValueList.reduce((coll, val) => {
  coll[val] = val;
  return coll;
}, {});

function isUint32(x) {
  return x === x >>> 0;
}

function validateUintField(fieldName, value) {
  if (isUint32(value)) return;
  throw new TypeError(`${fieldName} is required and must be a uint. Received ${value}`);
}
/**
 * @typedef {Object} Annotation
 * @property {AnnotationType}   type - Required. The type of annotation. ERROR/INFO/WARNING
 * @property {UInt32}           lineNumber - Required. The line number for the annotation.
 * @property {UInt32}           column - Required. The index in line text where the annotated text begins.
 * @property {UInt32}           endLineNumber - The line number on which the annotation ends.
 * @property {UInt32}           endColumn - The index in line text where the annotated text ends.
 * @property {String}           evidence - The text within the line text to be annotated.
 * @property {Node}             message - The message to be displayed for the annotation.
 */

/**
 * Model representing an annotation that can be used with the @annotations decorator.
 */


export default class Annotation {
  /**
   * @param  {AnnotationType}   options.type
   * @param  {UInt32}           options.lineNumber
   * @param  {UInt32}           options.column
   * @param  {UInt32}           options.endLineNumber
   * @param  {UInt32}           options.endColumn
   * @param  {Node}             options.message
   * @param  {String}           options.evidence
   * @return {Annotation}
   */
  constructor({
    type,
    lineNumber,
    column = 0,
    endLineNumber = null,
    endColumn = null,
    message = null,
    evidence = null
  }) {
    //  Required fields
    validateUintField('lineNumber', lineNumber);
    validateUintField('column', column);

    if (!(type in AnnotationTypeValueDict)) {
      throw new TypeError(`type is required and must be an AnnotationType. Received ${type}`);
    }
    /** @type {AnnotationType} type Required */


    this.type = type;
    /** @type {UInt32} lineNumber Required */

    this.lineNumber = lineNumber;
    /** @type {UInt32} column Required */

    this.column = column;
    /** @type {UInt32} */

    this.endLineNumber = endLineNumber;
    /** @type {UInt32} */

    this.endColumn = endColumn;
    /** @type {Node} */

    this.message = message;
    /** @type {String} */

    this.evidence = evidence;
    Object.freeze(this);
  }

}
/**
 * @type {Function}
 */

Annotation.shapePropType = PropTypes.shape({
  lineNumber: ReactUtilsPropTypes.uint32.isRequired,
  endLineNumber: ReactUtilsPropTypes.uint32,
  type: PropTypes.oneOf(AnnotationTypeValueList).isRequired,
  message: PropTypes.node,
  column: ReactUtilsPropTypes.uint32.isRequired,
  endColumn: ReactUtilsPropTypes.uint32,
  evidence: PropTypes.string
});
/**
 * @type {Function}
 */

Annotation.annotationsPropType = PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.instanceOf(Annotation), Annotation.shapePropType]));
/**
 * @param  {Object} error   HubL validation error
 * @return {Annotation}
 */

Annotation.fromHublValidation = function fromHublValidation({
  lineno,
  startPosition,
  severity,
  message,
  fieldName
}) {
  return new Annotation({
    lineNumber: lineno >= 1 && lineno === lineno >>> 0 ? lineno - 1 : 0,
    column: startPosition === startPosition >>> 0 ? startPosition : 0,
    type: severity === 'FATAL' ? AnnotationType.ERROR : AnnotationType.WARNING,
    message,
    evidence: fieldName
  });
};